import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, ChevronDown, MoreHorizontal, Zap, Eye, ArrowUpRight, Settings2, Search, Sparkles, Trash2 } from 'lucide-react';
import axios from 'axios';
import { ActionCard } from '../actions/ActionCard';
import { RailActions, RailBtn, RailMenu } from './ExpertRail';
import { useMoments } from '../filters/FiltersBar';
import { useIsMobile } from '../../hooks/useMobileLayout';
import { useInSheet } from '../ig/IgSheet';
import { AGENTS, AGENT_DOMAIN } from '../../utils/agents';
import { getApiUrl } from '../../utils/api';
import '../recipes/recettes-couts.css';
import '../finance/finance-phrase.css';
import '../finance/finance-hero.css';

// Standard « 360 Resto » commun à tous les modules (patron Recettes & coûts / Planning) :
// C1 en-tête groupé · C3 hero « décision de l'expert + fiabilité » · C5 rail unique. Aucune valeur par défaut inventée.

export const ModuleHeader = ({ expert = 'sabril', domaine, title, tagline, etat, children, counters = [], actions = [], testId = 'module-header' }) => {
  const name = AGENTS[expert]?.name || 'Sabril';
  const alerte = etat && (etat.tone === 'bad' || etat.tone === 'warn');
  // Gabarit IG : avatar rond de l'expert (anneau orange si alerte, gris sinon) · titre serif avec point · un seul slogan · ligne de navigation.
  return (
    <section className={`hp-card ig-profile ig-module-head ${alerte ? 'is-alert' : 'is-calm'}`} data-testid={testId}>
      <div className="flex items-start gap-4">
        <div className="ig-avatar" data-testid={`${testId}-avatar`} title={`${name} · ${domaine || AGENT_DOMAIN[expert] || ''}`}><span><img src={`/brand/agents/${expert}.webp?v=2`} alt="" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('b'), { textContent: name[0] })); }} /></span></div>
        <div className="min-w-0 flex-1">
          <h1 className="rc-title rc-title-2l" data-testid={`${testId}-title`}>{title}</h1>
          <p className="ig-module-tag" data-testid={`${testId}-expert`}>{tagline || `${name}, votre expert ${(domaine || AGENT_DOMAIN[expert] || 'pilotage').toLowerCase()}`}{etat && <> · <span className={`rc-leo-etat tone-${etat.tone || 'mid'}`} data-testid={`${testId}-etat`}>{etat.label}</span></>}</p>
          {counters.length > 0 && <div className="ig-counters" data-testid={`${testId}-compteurs`}>{counters.map((c) => <div key={c.key || c.label} role={c.onClick ? 'button' : undefined} tabIndex={c.onClick ? 0 : undefined} onClick={c.onClick} className={c.onClick ? 'is-click' : undefined} data-testid={c.testId}><b style={c.tone === 'danger' ? { color: '#C92A2A' } : c.tone === 'warn' ? { color: '#B25900' } : c.tone === 'ok' ? { color: '#1A7F37' } : undefined}>{c.value}</b><span>{c.label}</span></div>)}</div>}
        </div>
      </div>
      {actions.length > 0 && <div className="ig-profile-actions">{actions.map(({ label, Icon, onClick, dark, title: t, testId: tid }) => <button key={label || tid} type="button" onClick={onClick} title={t} aria-label={label || t} className={`btn-kit ${dark ? 'btn-kit-dark' : 'btn-kit-light'} ${label ? '' : 'is-ico'}`} data-testid={tid}>{Icon && <Icon size={16} />}{label ? ` ${label}` : null}</button>)}</div>}
      {children && <div className="finph rc-filters ig-module-nav" data-testid={`${testId}-filters`}>{children}</div>}
    </section>
  );
};

// Chip de la phrase-filtre (dark = valeur principale, link = secondaire). `onClick` optionnel.
export const PhraseChip = ({ children, dark, onClick, testId }) => (
  onClick
    ? <button type="button" className={`finph-chip ${dark ? 'finph-dark' : 'is-link'}`} onClick={onClick} data-testid={testId}>{children}</button>
    : <span className={`finph-chip ${dark ? 'finph-dark' : ''}`} data-testid={testId}>{children}</span>
);
export const PhraseSep = () => <span className="finph-sep-dot" aria-hidden>·</span>;

// Chip déroulant de la phrase-filtre : « tous les lieux ⌄ ». items : [{ key, label, on, onClick }].
export const PhraseMenu = ({ label, items = [], dark, testId = 'phrase-menu' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);
  return (
    <span className="finph-axe" ref={ref}>
      <button type="button" className={`finph-chip ${dark ? 'finph-dark' : 'is-link'}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={testId}>{label} <ChevronDown size={13} /></button>
      {open && <div className="finph-pop rc-pop" role="menu" data-testid={`${testId}-menu`}>
        {items.map((it) => <button key={it.key} type="button" role="menuitem" className={it.on ? 'is-on' : ''} onClick={() => { setOpen(false); it.onClick?.(); }} data-testid={`${testId}-${it.key}`}>{it.label}</button>)}
      </div>}
    </span>
  );
};

// Filtre « point de vente » propre à la page (remplace la FiltersBar globale) : lit/écrit `?pdv=` comme les axes salariés.
export const PdvChip = ({ testId = 'phrase-pdv' }) => {
  const [params, setParams] = useSearchParams();
  const { lieux } = useMoments();
  const pdv = params.get('pdv') || null;
  if (!lieux.length) return null;
  const set = (id) => { const p = new URLSearchParams(params); if (id) p.set('pdv', id); else p.delete('pdv'); setParams(p); };
  const cur = lieux.find((l) => l.lieu_id === pdv);
  return <PhraseMenu dark label={cur ? cur.name : 'tous les lieux'} testId={testId} items={[{ key: 'all', label: 'tous les lieux', on: !pdv, onClick: () => set(null) }, ...lieux.map((l) => ({ key: l.lieu_id, label: l.name, on: pdv === l.lieu_id, onClick: () => set(l.lieu_id) }))]} />;
};

// Fiabilité : ratio 0..1 (ou null si aucune donnée) → statut + texte, sans jamais afficher 100 % par défaut.
const pctTxt = (v) => (v < 1 && v * 100 >= 99.5 ? `${(v * 100).toFixed(1).replace('.', ',')}\u00a0%` : `${Math.round(v * 100)}\u00a0%`);
export const niveau = (r) => (r == null ? 'none' : r >= 0.95 ? 'ok' : r >= 0.5 ? 'warn' : 'bad');
export const fiabLine = (key, label, ratio, { go, none = 'aucune donnée', ok = 'à jour', text } = {}) => ({
  key, label, status: niveau(ratio),
  text: text != null ? text : ratio == null ? none : ratio >= 1 ? ok : pctTxt(ratio),
  go: ratio != null && ratio < 1 && go ? go : null,
});

const court = (v) => Math.round(v).toLocaleString('fr-FR');
const Ligne = ({ l, testId }) => (
  <button type="button" className={`rc-fiab-row tone-${l.status}`} onClick={l.go || undefined} disabled={!l.go} data-testid={`${testId}-fiab-${l.key}`}>
    <span className={`rc-fiab-ico tone-${l.status}`} aria-hidden>{l.status === 'ok' ? <Check size={11} strokeWidth={3.5} /> : '!'}</span>
    <span className="rc-fiab-label">{l.label}</span>
    <span className="rc-fiab-val" data-testid={`${testId}-fiab-${l.key}-val`}>{l.text}</span>
  </button>
);

// Hero 2 zones : gauche = 1re décision de l'expert (même source que la page Actions) ; droite = fiabilité calculée.
// `score` : nombre 0..100 ou null (→ « — »). `decision` : undefined = chargement, null = rien à traiter.
export const ModuleDecisionHero = ({ expert = 'sabril', decision, onDecisionDone, lignes = [], score, scoreUnit = '%', scoreLabel = 'fiable', panelTitle = 'Fiabilité des données', vide, testId = 'module-hero' }) => {
  const name = AGENTS[expert]?.name || 'Sabril';
  const etat = score == null ? { label: 'à renseigner', cls: 'tone-warn' } : score >= 95 ? { label: 'fiable', cls: 'tone-ok' } : score >= 50 ? { label: 'provisoire', cls: 'tone-warn' } : { label: 'alerte', cls: 'tone-bad' };
  const ring = (r) => (r == null ? 0 : Math.max(0, Math.min(100, Math.round(r * 100))));
  // Ligne d'état IG : anneaux de progression (fiabilité) + carte compacte de l'expert, sur une seule ligne.
  return (
    <section className="ig-etat" data-testid={testId}>
      <div className="ig-rings" aria-label={panelTitle} data-testid={`${testId}-fiabilite`}>
        <button type="button" className="ig-ring-item" data-testid={`${testId}-score`} title={`${panelTitle} · ${etat.label}`}>
          <span className={`ig-ring ${etat.cls}`} style={{ '--p': score == null ? 0 : Math.round(score) }}><b>{score == null ? '—' : `${Math.round(score)}${scoreUnit === '%' ? ' %' : scoreUnit}`}</b></span>
          <small>{scoreLabel}</small>
        </button>
        {lignes.map((l) => {
          const n = niveau(l.ratio);
          // Sans pourcentage : pas d'anneau — le chiffre seul (≤ 4 caractères) ou une icône d'état, le libellé à côté.
          const brut = l.text == null ? '' : String(l.text).trim();
          const chiffre = /^[\d\s.,]+$/.test(brut) ? court(Number(brut.replace(/\s/g, '').replace(',', '.'))) : null;
          const tone = l.ratio == null ? (l.status === 'ok' ? 'ok' : l.status === 'none' || l.status === 'bad' ? 'warn' : l.status || 'none') : n;
          return <button key={l.key} type="button" className="ig-ring-item" onClick={l.go || undefined} disabled={!l.go} title={`${l.label}${brut ? ` · ${brut}` : ''}`} data-testid={`${testId}-ligne-${l.key}`}>
            {l.ratio == null
              ? <span className={`ig-ring is-flat tone-${tone}`}>{chiffre != null ? <b>{chiffre}</b> : tone === 'ok' ? <Check size={20} strokeWidth={3} /> : <AlertCircle size={20} />}</span>
              : <span className={`ig-ring tone-${n}`} style={{ '--p': ring(l.ratio) }}><b>{`${Math.round(l.ratio * 100)} %`}</b></span>}
            <small>{l.label}</small>
          </button>;
        })}
      </div>
      <div className="ig-etat-card" data-testid={`${testId}-decision`}>
        {decision === undefined && <div className="fhero-skel" data-testid={`${testId}-decision-loading`} />}
        {decision === null && (
          <div className="ig-etat-clear" data-testid={`${testId}-decision-empty`}>
            <span className="ig-etat-ava"><img src={`/brand/agents/${expert}.webp?v=2`} alt="" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('b'), { textContent: name[0] })); }} /></span>
            <div className="min-w-0">
              <p className="ig-etat-k">{name} · rien d’urgent</p>
              <p className="ig-etat-t">{vide?.title || 'Rien à décider pour l’instant.'}</p>
            </div>
            <span className="ig-etat-ok" aria-hidden><Check size={16} strokeWidth={3} /></span>
          </div>
        )}
        {decision && <ActionCard a={decision} compact onTrace={onDecisionDone} />}
      </div>
    </section>
  );
};

// Décisions d'un expert (source unique : /actions/proposees). La 1re (urgente d'abord) alimente le hero, les autres le rail.
export const useExpertDecisions = (agent, { modules } = {}) => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [alerts, setAlerts] = useState(undefined);
  const modKey = Array.isArray(modules) ? modules.join(',') : '';
  const agentKey = Array.isArray(agent) ? agent.join(',') : agent;
  const reload = useCallback(() => axios.get(`${getApiUrl()}/actions/proposees`, { withCredentials: true })
    .then((r) => { const mods = modKey ? modKey.split(',') : null; const agents = agentKey.split(','); setAlerts((r.data?.alerts || []).filter((a) => agents.includes(a.agent) && !a.done && (!mods || mods.includes(a.module)))); })
    .catch(() => setAlerts([])), [agentKey, modKey]);
  useEffect(() => { reload(); }, [reload]);
  const decision = alerts === undefined ? undefined : !alerts.length ? null : (alerts.find((a) => a.groupe === 'urgent') || alerts[0]);
  const others = useMemo(() => (alerts || []).filter((a) => a !== decision).map((a) => ({
    key: a.key, tone: a.groupe === 'urgent' ? 'urgent' : 'semaine', pill: a.groupe === 'urgent' ? 'urgent' : 'cette semaine',
    title: a.title, sub: a.fact, action: 'Voir le détail',
    onClick: () => navigate(a.path?.startsWith('/') ? a.path : `/${tenantSlug}/actions?focus=${a.key}`),
  })), [alerts, decision, navigate, tenantSlug]);
  const toActions = alerts?.length ? () => navigate(`/${tenantSlug}/actions`) : null;
  return { alerts, decision, others, reload, toActions };
};

// Alertes locales d'une page ({ key, n, tag, desc, tone, onClick }) → décisions du rail (masquées à 0, « good » exclu).
export const decisionsFromAlertes = (items = [], actionLabel = {}) => (items || [])
  .filter((a) => a && a.n > 0 && a.tone !== 'good')
  .map((a) => ({
    key: a.key || a.tag, tone: a.tone === 'critique' ? 'urgent' : 'semaine', pill: a.tone === 'critique' ? 'urgent' : 'cette semaine',
    title: `${a.n} ${a.tag}`, sub: a.desc, action: actionLabel[a.key] || a.action || 'Voir', onClick: a.onClick,
  }));

// Rail « Actions » à partir d'une liste d'actions ({ key, label, onClick | to, menu, disabled, title, count }) : boutons blancs, menus déroulants.
export const RailActionsList = ({ actions = [], lead, foot, testId = 'rail-actions' }) => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const mobile = useIsMobile();
  const inSheet = useInSheet();
  const go = (to) => navigate(/^https?:/.test(to) ? to : `/${tenantSlug}/${String(to).replace(/^\//, '')}`);
  const list = actions.filter(Boolean);
  // Mobile iOS (règle 3) : pas de grille de boutons → recherche + un seul menu « ⋯ Actions ».
  useEffect(() => {
    if (!mobile) return undefined;
    const close = (e) => document.querySelectorAll('.ab-overflow[open]').forEach((d) => {
      if (!d.querySelector('.ab-overflow-list')?.contains(e.target) && !d.querySelector('summary')?.contains(e.target)) d.removeAttribute('open');
    });
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [mobile]);
  if (inSheet || !mobile) {
    const KIND_ICON = { do: Zap, see: Eye, out: ArrowUpRight, set: Settings2, find: Search, analyse: Sparkles, danger: Trash2 };
    const btn = (key, label, sub, a, onClick) => <RailBtn key={key} Icon={a.Icon || KIND_ICON[a.kind] || Eye} sub={sub} danger={a.danger} sep={a.sep} count={a.count} onClick={onClick} disabled={a.disabled} title={a.title} arrow={a.kind === 'out' && !a.menu} testId={`${testId}-${key}`}>{label}</RailBtn>;
    return (
      <RailActions testId={testId} lead={lead} foot={foot}>
        {list.flatMap((a) => a.menu?.length
          ? a.menu.map((m, i) => btn(`${a.key}-${m.key}`, m.label, m.sub || a.label, { ...a, Icon: m.Icon || a.Icon, sep: a.sep && i === 0, disabled: a.disabled || m.disabled }, () => (m.to ? go(m.to) : m.onClick?.())))
          : [btn(a.key, a.label, a.sub, a, () => (a.to ? go(a.to) : a.onClick?.()))])}
      </RailActions>
    );
  }
  if (mobile && list.length) {
    const shut = () => document.querySelectorAll('.ab-overflow[open]').forEach((d) => d.removeAttribute('open'));
    const rows = list.map((a) => a.menu?.length
      ? a.menu.map((m) => <button key={`${a.key}-${m.key}`} type="button" role="menuitem" disabled={m.disabled} onClick={() => { shut(); m.to ? go(m.to) : m.onClick?.(); }} data-testid={`${testId}-${a.key}-${m.key}`}><span>{a.label} · {m.label}</span></button>)
      : <button key={a.key} type="button" role="menuitem" disabled={a.disabled} onClick={() => { shut(); a.to ? go(a.to) : a.onClick?.(); }} data-testid={`${testId}-${a.key}`}><span>{a.label}</span>{a.count != null && a.count !== '' ? <b className="ab-count">{a.count}</b> : null}</button>);
    // Dans la feuille « roue » : toutes les actions à plat, une par ligne (pas de menu dans un menu).
    if (inSheet) return <RailActions testId={testId} lead={lead} foot={foot}><div className="ab-overflow-list is-flat" role="menu" data-testid={`${testId}-flat`}>{rows}</div></RailActions>;
    return (
      <RailActions testId={testId} lead={lead} foot={foot}>
        <details className="ab-overflow" data-testid={`${testId}-overflow`}>
          <summary className="ab-overflow-sum" data-testid={`${testId}-overflow-trigger`}><MoreHorizontal size={16} aria-hidden /> Actions<b className="ab-overflow-n">{list.length}</b></summary>
          <div className="ab-overflow-list" role="menu">{rows}</div>
        </details>
      </RailActions>
    );
  }
  return (
    <RailActions testId={testId} lead={lead} foot={foot}>
      {list.map((a) => a.menu?.length
        ? <RailMenu key={a.key} label={a.label} disabled={a.disabled} testId={`${testId}-${a.key}`} items={a.menu.map((m) => ({ key: m.key, label: m.label, disabled: m.disabled, onClick: () => (m.to ? go(m.to) : m.onClick?.()) }))} />
        : <RailBtn key={a.key} onClick={() => (a.to ? go(a.to) : a.onClick?.())} disabled={a.disabled} title={a.title} arrow={a.kind === 'out'} testId={`${testId}-${a.key}`}>
            {a.label}{a.count != null && a.count !== '' ? <> · <span className="rc-rail-n">{a.count}</span></> : null}
          </RailBtn>)}
    </RailActions>
  );
};
